import { useEffect } from "react";

const BASE_URL = "https://starryhwa.com";
const DEFAULT_TITLE = "Star.Ryhwa | Handcrafted K-Pop Merch, Decoden & Fanmade Goods";
const DEFAULT_DESCRIPTION =
  "Shop handcrafted K-pop photocards, custom decoden keychains, vinyl stickers, tote bags, enamel pins, and fanmade collectibles at Star.Ryhwa.";
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;

/**
 * Lightweight dynamic SEO component for managing title, meta tags, and JSON-LD in head
 */
export default function SEO({
  title,
  description,
  canonicalUrl,
  image,
  type = "website",
  jsonLd = null,
}) {
  useEffect(() => {
    // 1. Page Title
    const formattedTitle = title
      ? title.includes("Star.Ryhwa")
        ? title
        : `${title} | Star.Ryhwa`
      : DEFAULT_TITLE;
    document.title = formattedTitle;

    // Helper to update or create meta tags
    const setMetaTag = (attrName, attrValue, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Helper for link tags
    const setLinkTag = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const desc = description || DEFAULT_DESCRIPTION;
    const img = image ? (image.startsWith("http") ? image : `${BASE_URL}${image}`) : DEFAULT_IMAGE;
    const url = canonicalUrl ? (canonicalUrl.startsWith("http") ? canonicalUrl : `${BASE_URL}${canonicalUrl}`) : window.location.href;

    // 2. Standard Meta Tags
    setMetaTag("name", "description", desc);
    setLinkTag("canonical", url);

    // 3. Open Graph
    setMetaTag("property", "og:title", formattedTitle);
    setMetaTag("property", "og:description", desc);
    setMetaTag("property", "og:image", img);
    setMetaTag("property", "og:url", url);
    setMetaTag("property", "og:type", type);

    // 4. Twitter
    setMetaTag("name", "twitter:title", formattedTitle);
    setMetaTag("name", "twitter:description", desc);
    setMetaTag("name", "twitter:image", img);

    // 5. Dynamic JSON-LD Structured Data
    let scriptTag = document.getElementById("dynamic-jsonld");
    if (jsonLd) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = "dynamic-jsonld";
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(jsonLd);
    } else if (scriptTag) {
      scriptTag.remove();
    }

    return () => {
      // Cleanup dynamic JSON-LD on unmount
      const existingScript = document.getElementById("dynamic-jsonld");
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [title, description, canonicalUrl, image, type, jsonLd]);

  return null;
}
